// Where TRM data comes from.
//
// A customer running Flexera and ServiceNow will ask which of *their* systems a
// number came from. "Ours" is the wrong answer, and the record counts here are
// queried rather than asserted — a registry claiming a feed it cannot evidence
// is the same failure as a conformance report passing an unanswerable principle.
import React, { useEffect, useState } from "react";
import { Database, CheckCircle2, Circle, Zap } from "lucide-react";
import * as api from "../../api/sources";

const STATUS = {
  connected:     { fg: "var(--cares-green)", bg: "color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))", label: "Connected", Icon: CheckCircle2 },
  native:        { fg: "var(--cares-purple)", bg: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-purple) 30%, var(--cares-surface-card))", label: "Native",    Icon: Zap },
  configured:    { fg: "var(--cares-amber)", bg: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", label: "Configured", Icon: Circle },
  not_connected: { fg: "var(--cares-orange)", bg: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", label: "Not connected", Icon: Circle },
};

const ago = (ts) => {
  if (!ts) return "never";
  const h = Math.round((Date.now() - new Date(ts).getTime()) / 36e5);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export default function DataSourcesPage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.getSources().then(setD).catch((e) => setErr(e.message || String(e))); }, []);

  if (err) return <div style={{ padding: 20, color: "var(--cares-orange)" }}>{err}</div>;
  if (!d) return <div style={{ padding: 20, color: "var(--cares-text-label)" }}>Loading…</div>;

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cares-text-1)", margin: "0 0 4px",
                   display: "flex", alignItems: "center", gap: 9 }}>
        <Database size={20} style={{ color: "var(--cares-purple)" }} /> Data Sources
      </h1>
      <p style={{ fontSize: 13, color: "var(--cares-text-label)", margin: "0 0 16px", maxWidth: 780 }}>
        Every figure in the technology reference model is attributable to the system
        it came from. {d.count} sources, {d.recordsAttributed.toLocaleString()} records
        stamped with their origin.
      </p>

      <div style={{ background: "var(--cares-surface-board)", border: "1px solid var(--cares-line-card)", borderRadius: 8,
                    padding: "13px 15px", marginBottom: 18, maxWidth: 900 }}>
        <div style={{ fontSize: 12.5, color: "var(--cares-text-2)", lineHeight: 1.6 }}>
          <strong>System of record.</strong> {d.posture.systemOfRecord}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--cares-text-2)", lineHeight: 1.6, marginTop: 6 }}>
          <strong>Read, not re-derived.</strong> {d.posture.readOnly}
        </div>
        <div style={{ fontSize: 12, color: "var(--cares-text-label)", lineHeight: 1.6, marginTop: 8,
                      paddingTop: 8, borderTop: "1px solid var(--cares-line-card)" }}>
          {d.posture.why}
        </div>
      </div>

      <div style={{ display: "grid", gap: 11 }}>
        {d.sources.map((s) => {
          const st = STATUS[s.connectorStatus] || STATUS.not_connected;
          return (
            <div key={s.sourceId}
                 style={{ background: "var(--cares-surface-card)", border: "1px solid var(--cares-line-card)", borderRadius: 8,
                          padding: 15, borderLeft: `3px solid ${st.fg}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--cares-text-1)" }}>{s.sourceName}</span>
                {s.vendor && (
                  <span style={{ fontSize: 11.5, color: "var(--cares-text-note)" }}>{s.vendor}</span>
                )}
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                               color: st.fg, background: st.bg, border: `1px solid ${st.bd}` }}>
                  {st.label}
                </span>
                <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--cares-text-note)",
                               fontFamily: "monospace" }}>
                  {s.syncFrequency} &middot; {ago(s.lastSyncAt)}
                </span>
              </div>

              <div style={{ fontSize: 12.5, color: "var(--cares-text-3)", margin: "6px 0 9px" }}>
                {s.description}
              </div>

              <div style={{ fontSize: 12, color: "var(--cares-text-1)", marginBottom: 8 }}>
                <strong>Authoritative for:</strong>{" "}
                <span style={{ color: "var(--cares-text-3)" }}>{s.authoritativeFor}</span>
              </div>

              {s.feeds.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                  {s.feeds.map((f, i) => (
                    <span key={i} style={{ fontFamily: "monospace", fontSize: 11,
                                           border: "1px solid var(--cares-line-card)", borderRadius: 3,
                                           padding: "3px 8px", color: "var(--cares-text-3)" }}>
                      {f.entity}: <strong style={{ color: "var(--cares-purple)" }}>
                        {(f.records || 0).toLocaleString()}
                      </strong>
                      {f.detail ? ` · ${f.detail}` : ""}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 11.5, color: "var(--cares-text-label)", lineHeight: 1.55 }}>{s.notes}</div>
              <div style={{ fontSize: 10.5, color: "var(--cares-text-note)", marginTop: 6 }}>
                Owner: {s.owner}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
